import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Math as MathFormula } from "@/components/Math";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function KineticEnergyCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Kinetic Energy Calculator", url: "https://calclab.app/calculator/kinetic-energy" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Kinetic Energy Calculator",
      "Calculate the energy of motion using KE = ½mv². Free online physics calculator.",
      "https://calclab.app/calculator/kinetic-energy",
      lang
    )
  ]);
  const [mass, setMass] = useState("");
  const [velocity, setVelocity] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateKineticEnergy = () => {
    const m = parseFloat(mass);
    const v = parseFloat(velocity);

    if (isNaN(m) || isNaN(v)) {
      toast.error("Please enter valid values");
      return;
    }

    const ke = 0.5 * m * v * v;
    setResult(ke);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setMass("");
    setVelocity("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Kinetic Energy Calculator" 
      calculatorPath="/calculator/kinetic-energy"
      category="physics"
      subcategory="Energy"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Kinetic Energy Calculator</h1>
          <p className="text-muted-foreground">
            Calculate the energy of motion
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: KE = ½mv²</CardTitle>
            <CardDescription>
              Where KE is kinetic energy (J), m is mass (kg), and v is velocity (m/s)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mass">Mass (kg)</Label>
                <Input
                  id="mass"
                  type="number"
                  step="0.01"
                  placeholder="Enter mass"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="velocity">Velocity (m/s)</Label>
                <Input
                  id="velocity"
                  type="number"
                  step="0.01"
                  placeholder="Enter velocity"
                  value={velocity}
                  onChange={(e) => setVelocity(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateKineticEnergy} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} J
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Kinetic Energy (Joules)
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["kinetic-energy"]} trigger={result} />
              </>
            )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-sm">Formula</CardTitle>
                </CardHeader>
                <CardContent>
                  <MathFormula block>{String.raw`KE = \frac{1}{2}mv^2`}</MathFormula>
                  <p className="text-sm text-muted-foreground mt-2">
                    Kinetic Energy = ½ × mass × velocity²
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Enter the mass of the object in kilograms</p>
                  <p>2. Enter the velocity in meters per second</p>
                  <p>3. Click Calculate to get kinetic energy in Joules</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: 2 kg object moving at 5 m/s = 25 J kinetic energy
                  </p>
                </CardContent>
              </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
